// Implements: F-PRV-006, F-PRV-011, F-PRV-016
import { useLayoutEffect } from "react";
import type { ReactElement, ReactNode, RefObject } from "react";

import styles from "./PreviewViewport.module.css";

export interface PreviewViewportProps {
  /** The frame's LAYOUT viewport in CSS pixels: what its media queries evaluate against. */
  readonly width: number;
  /** How large the frame looks on screen; it never changes `width`. */
  readonly zoom: number;
  /** The scrolling stage, for the container that measures the space available. */
  readonly stageRef: RefObject<HTMLDivElement | null>;
  /**
   * The stage's name. It scrolls a frame that can be wider than the space, so it is a named region
   * in the tab order: arrows and Page keys scroll it from the keyboard (WCAG 2.1.1, axe
   * `scrollable-region-focusable`); the frame inside is not focusable content for that rule.
   */
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * The stage the frame sits in.
 *
 * The width reaches the CSS as custom properties set from script (`style.setProperty`), because the
 * SPA's `style-src` has no `'unsafe-inline'` and a JSX `style` attribute is banned for that reason.
 * The frame is as wide as `--preview-width`, so the document inside lays out at that width and its
 * own media queries apply, which is what makes the mobile treatment a real 375 px frame. Zoom is a
 * transform on a wrapper and is applied only when it is not 1, so a width change never rescales.
 */
export function PreviewViewport({
  width,
  zoom,
  stageRef,
  label,
  children,
}: PreviewViewportProps): ReactElement {
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (stage === null) return;
    stage.style.setProperty("--preview-width", `${String(width)}px`);
    stage.style.setProperty("--preview-zoom", String(zoom));
  }, [stageRef, width, zoom]);

  const scaled = zoom !== 1;
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrolling region must be reachable to be scrolled by keyboard
    <div className={styles.stage} ref={stageRef} data-testid="preview-stage" role="region" aria-label={label} tabIndex={0}>
      <div className={styles.sizer}>
        <div
          className={styles.scaler}
          data-testid="preview-scaler"
          data-scaled={scaled ? "true" : undefined}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
